import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Building2, Layers, Globe2, Network } from "lucide-react";
import { useLang } from "@/lib/i18n";

const HERO_BG_IMAGES = [
  // Process Optimization — industrial gears / manufacturing
  "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=2400&q=80",
  // Defense & Security — radar / control
  "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2400&q=80",
  // Supply Chain — containers / logistics
  "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=2400&q=80",
  // Industrial AI & Robotics — robot / AI
  "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=2400&q=80",
];

const STAT_ICONS = [Building2, Layers, Globe2, Network];

export default function Hero() {
  const { t, dict } = useLang();
  const stats = dict.hero.stats;
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setBgIndex((i) => (i + 1) % HERO_BG_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen overflow-hidden flex flex-col" style={{ backgroundColor: "#0A192F" }}>
      {/* Crossfading background images representing the 4 disciplines */}
      {HERO_BG_IMAGES.map((src, i) => (
        <div
          key={src}
          aria-hidden="true"
          className="absolute inset-0 bg-center bg-cover transition-opacity duration-[2000ms] ease-in-out pointer-events-none"
          style={{
            backgroundImage: `url(${src})`,
            opacity: i === bgIndex ? 0.33 : 0,
            mixBlendMode: "luminosity",
          }}
        />
      ))}
      {/* Dark blue tint to keep text readable */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F]/85 via-[#0A192F]/70 to-[#0A192F]/90 pointer-events-none" />

      {/* Subtle tech grid overlay for depth without distracting from solid blue */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-44 lg:pt-52 pb-12 flex flex-col items-center justify-center text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-6xl flex flex-col items-center">
          <div className="flex w-full max-w-2xl items-center justify-center gap-3 px-5 py-3 sm:py-3.5 rounded-full bg-white/10 border border-[#C9A96E]/40 backdrop-blur-sm mb-6 sm:mb-8" data-testid="hero-badge">
            <Sparkles size={16} className="text-[#C9A96E] flex-shrink-0" />
            <span className="text-sm sm:text-base uppercase tracking-[0.22em] sm:tracking-[0.28em] text-white font-semibold text-center">
              {t("hero.badge")}
            </span>
          </div>

          <h1 data-testid="hero-headline" className="font-heading font-bold text-white text-3xl sm:text-4xl lg:text-6xl leading-[1.1] tracking-tight">
            <span className="block whitespace-normal lg:whitespace-nowrap">
              {t("hero.title_a")} <span className="text-[#C9A96E]">{t("hero.title_a_accent")}</span>
            </span>
            <span className="block whitespace-normal lg:whitespace-nowrap">
              {t("hero.title_b")} <span className="text-[#C9A96E]">{t("hero.title_b_accent")}</span>
            </span>
          </h1>

          <div data-testid="hero-subheadline" className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl lg:max-w-5xl leading-relaxed font-light space-y-4 lg:whitespace-pre-line">
            {t("hero.subtitle").split("\n\n").map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="#contact" data-testid="hero-cta-contact" className="group inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-white text-[#0A192F] font-semibold rounded-full hover:bg-[#3B82F6] hover:text-white transition-all duration-300 text-sm sm:text-base">
              {t("hero.cta_contact")}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#services" data-testid="hero-cta-services" className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300 text-sm sm:text-base">
              {t("hero.cta_services")}
            </a>
          </div>
        </motion.div>
      </div>

      {/* Value props — flow naturally at the bottom of the hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="relative max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-12 lg:pb-14"
        data-testid="hero-stats"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 pt-6 sm:pt-8 border-t border-white/10">
          {stats.map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <div
                key={s.t}
                className="group h-full flex flex-col items-center text-center p-4 sm:p-5 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#C9A96E]/50 hover:bg-white/[0.06] transition-all duration-300"
              >
                <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#C9A96E]/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={16} className="text-[#C9A96E]" />
                </span>
                <div className="mt-3 sm:mt-4 font-heading text-[11px] sm:text-[13px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.16em] text-white leading-tight whitespace-nowrap">
                  {s.t}
                </div>
                <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-white/70 leading-snug">
                  {s.s}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}
